<template>
    <b-card
        bg-variant="primary"
        text-variant="white"
        no-body
        class="mb-0"
    >
        <b-card-header class="p-75">
            <div class="p-1 d-flex align-items-center">
                <!-- {{ selectedMonthRange }} -->
                <CommonSelectCalendarMonthRange
                    v-if="selectedMonthRange"
                    class="mb-0"
                    storeKey="$_costPivot"
                    size="sm"
                />
            </div>
            <b-dropdown
                variant="link"
                toggle-class="p-0 mr-50"
                no-caret
                right
                :disabled="!months.length"
            >
                <template v-slot:button-content>
                    <feather-icon
                        icon="MoreVerticalIcon"
                        class="text-white"
                    />
                </template>
                <b-dropdown-item v-if="months.length >= 6" @click="recentlySearch(6)">
                    {{ $t('v4.Last 6 months') }}
                </b-dropdown-item>
                <b-dropdown-item v-if="months.length >= 3" @click="recentlySearch(3)">
                    {{ $t('v4.Last 3 months') }}
                </b-dropdown-item>
                <b-dropdown-item @click="recentlySearch(1)">
                    {{ $t('v4.Last month') }}
                </b-dropdown-item>
            </b-dropdown>
        </b-card-header>
        <div class="px-1 pt-0 pb-75">
            <!-- {{ rangeValue }} -->
            <VueSlider
                v-model="rangeValue"
                :railStyle="sliderStyle.railStyle"
                :processStyle="sliderStyle.processStyle"
                :dotStyle="sliderStyle.dotStyle"
                :data="months"
                :lazy="true"
                data-label="label"
                data-value="value"
                tooltipPlacement="bottom"
                :marks="marks"
                :disabled="disabledValue"
                @change="sliderChange"
            >
            </VueSlider>
        </div>
    </b-card>
</template>

<script>
import VueSlider from 'vue-slider-component'
import moment from 'moment'
import { computed, toRefs, ref, watch } from '@vue/composition-api'
import useCommonData from '@/composables/useCommonData'
import useSelectMonthRange from '@/composables/useSelectMonthRange'
import CommonSelectCalendarMonthRange from '@/components/commonSelect/CommonSelectCalendarMonthRange'

export default {
    components: {
        CommonSelectCalendarMonthRange,
        VueSlider
    },
    props: {
        loading: {
            type: Boolean,
            default: false
        },
        indexes: {
            type: Array,
            default: function () {
                return []
            }
        },
        data: {
            type: Object,
            default: function () {
                return {}
            }
        },
        maxPeriod: {
            type: Number,
            default: 6
        },
        theme: {
            type: String,
            default: ''
        },
        themeColors: {
            type: Object,
            default: function () {
                return {}
            }
        },
        downloadMode: {
            type: Boolean,
            default: false
        }
    },
    setup (props, context) {
        const {
            selectedMonthRange,
            selectMonthRange,
            minDate,
            maxDate
        } = useSelectMonthRange('$_costPivot')
        const { themeColors, loading, indexes } = toRefs(props)
        const sliderStyle = {
            railStyle: {
                'background-color': themeColors.value.light
            },
            processStyle: {
                'background-color': themeColors.value.warning
            },
            dotStyle: {
                'background-color': themeColors.value.warning
            }
        }
        const defaultPeriod = 3
        const { __enMonthMap } = useCommonData()
        const duration = moment.duration({ 'months': 1 })
        const startRange = ref(moment(minDate.value, 'YYYY-MM'))
        const endRange = ref(moment(maxDate.value, 'YYYY-MM'))
        const startMonth = computed({
            get () {
                return startRange.value
            },
            set (value) {
                startRange.value = value
            }
        })
        const endMonth = computed({
            get () {
                return endRange.value
            },
            set (value) {
                endRange.value = value
            }
        })
        const months = computed(() => {
            if (!minDate.value && !maxDate.value) return []
            let i = 0
            const arr = []
            let month = moment(minDate.value, 'YYYY-MM')
            const end = moment(maxDate.value, 'YYYY-MM')
            while (month <= end) {
                arr.push({
                    label: month.format('YYYY-MM'),
                    value: i
                })
                i++
                month = month.add(duration)
            }
            return arr
        })
        const disabledValue = computed(() => loading.value || isOneMonth.value || !months.value.length ? 'disabled' : undefined)
        const maxRange = computed(() => months.value.length - 1)
        const rangeValue = ref([Math.max(maxRange.value - defaultPeriod + 1, 0), maxRange.value])
        const isOneMonth = computed(() => months.value.length === 1)
        const startMonthString = computed(() => {
            const startMonth = months.value[Math.min(...rangeValue.value)]
            return months.value.length ? __enMonthMap[startMonth.label.substring(5, 7)] + startMonth.label.substring(0, 4) : ''
        })
        const endMonthString = computed(() => {
            const endMonth = months.value[Math.max(...rangeValue.value)]
            return months.value.length ? __enMonthMap[endMonth.label.substring(5, 7)] + endMonth.label.substring(0, 4) : ''
        })
        /**
         *
         * @param {Number} period period short select (1 or 3 or 6)
         */
        const recentlySearch = (period) => {
            const findValue = months.value.find(month => month.label === endMonth.value.format('YYYY-MM')).value
            const startRange = findValue - period + 1
            const endRange = findValue
            rangeValue.value = [startRange, endRange]
            const indexes = months.value.slice(startRange, endRange + 1).map(item => item.label)
            selectMonthRange(indexes)
        }
        /**
         *
         * @param {Array} value Range value [start, end]
         * @param {Number} index slide direction (0 = left, 1 = right)
         */
        const sliderChange = (value, index) => {
            if (value[0] > value[1]) return
            let startRange = value[1] - value[0] > 5 ? value[1] - 5 : value[0]
            let endRange = value[1]
            if (index === 0) {
                startRange = value[0]
                endRange = value[1] - value[0] > 5 ? value[0] + 5 : value[1]
            }
            rangeValue.value = [startRange, endRange]
            const indexes = months.value.slice(startRange, endRange + 1).map(item => item.label)
            selectMonthRange(indexes)
        }
        watch(indexes, (value, oldValue) => {
            if (!value || !value.length) return
            // months 배열이 준비되었는지 확인
            if (!months.value || !months.value.length) {
                return
            }

            // nextTick을 사용하여 months가 업데이트된 후 실행
            context.root.$nextTick(() => {
                const startRange = months.value.find(month => month.label === value[0])?.value
                const endRange = months.value.find(month => month.label === value[value.length - 1])?.value
                if (startRange !== undefined && endRange !== undefined) {
                    rangeValue.value = [startRange, endRange]
                }
            })
        }, {
            deep: true,
            immediate: true
        })
        watch([minDate, maxDate], (value, oldValue) => {
            if (startMonth.value !== moment(value[0], 'YYYY-MM')) {
                startMonth.value = moment(value[0], 'YYYY-MM')
            }
            if (endMonth.value !== moment(value[1], 'YYYY-MM')) {
                endMonth.value = moment(value[1], 'YYYY-MM')
            }
            // 다운로드 모드가 아닐 때만 기본값으로 리셋
            // 다운로드 모드일 때는 indexes prop 변경에 의해 설정됨
            if (!props.downloadMode) {
                rangeValue.value = [Math.max(maxRange.value - defaultPeriod + 1, 0), maxRange.value]
            }
        }, {
            deep: true
            // immediate: true
        })

        // 다운로드 모드 변경 시 indexes prop을 기반으로 슬라이더 업데이트
        watch(() => props.downloadMode, (newVal, oldVal) => {
            if (newVal && indexes.value && indexes.value.length) {
                const startRange = months.value.find(month => month.label === indexes.value[0])?.value
                const endRange = months.value.find(month => month.label === indexes.value[indexes.value.length - 1])?.value
                if (startRange !== undefined && endRange !== undefined) {
                    rangeValue.value = [startRange, endRange]
                }
            }
        })

        // months가 변경되었을 때 현재 indexes를 기반으로 슬라이더 업데이트
        watch(months, (newMonths, oldMonths) => {
            if (!newMonths || !newMonths.length) return
            if (!indexes.value || !indexes.value.length) return

            // 다운로드 모드에서만 동작
            if (props.downloadMode) {
                const startRange = newMonths.find(month => month.label === indexes.value[0])?.value
                const endRange = newMonths.find(month => month.label === indexes.value[indexes.value.length - 1])?.value
                if (startRange !== undefined && endRange !== undefined) {
                    rangeValue.value = [startRange, endRange]
                }
            }
        }, {
            deep: true
        })

        const marks = (value) => {
            // const n = months.value.length % 5
            // if (value % n === 0) return { label: __enMonthMap[months.value[value].label.substring(5, 7)] }
            // return ''
            // return { label: __enMonthMap[months.value[value].label.substring(5, 7)] }
        }
        return {
            selectedMonthRange,
            isOneMonth,
            startMonthString,
            endMonthString,
            marks,
            rangeValue,
            months,
            __enMonthMap,
            sliderStyle,
            disabledValue,
            sliderChange,
            recentlySearch
        }
    }
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-slider.scss';
</style>
